<template>
  <div style="padding: 2rem; display: flex; justify-content: center">
    <div style="max-width: 500px; width: 100%; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif">
      <div style="display: flex; align-items: center; gap: 2rem; flex-wrap: wrap">
        <label style="display: flex; align-items: center; gap: 0.5rem; font-size: 1.1rem">
          <input type="checkbox" v-model="isListening" class="custom-checkbox" />
          <span>Speech</span>
        </label>
        <div>
          <select
            id="source-lang"
            v-model="selectedLang"
            style="padding: 0.5rem 1rem; font-size: 1rem; border: 1px solid #ccc; border-radius: 6px; background-color: white; max-width: 160px">
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">
              {{ lang.name }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <div
      v-if="showModal"
      style="
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        transition: background 0.3s ease;
      ">
      <div
        style="
          background: #fff;
          padding: 2rem 2.5rem;
          border-radius: 12px;
          max-width: 420px;
          width: 90%;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          text-align: center;
          animation: fadeInScale 0.3s ease;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        ">
        <h3 style="margin: 0 0 1rem; font-size: 1.5rem; color: #333">
          {{ isListening ? 'Speech Mode Enabled' : 'Speech Mode Disabled' }}
        </h3>
        <p style="color: #555; font-size: 1rem; margin-bottom: 1.5rem">
          {{ isListening ? 'Please mute Zoom to avoid echo or overlapping audio.' : 'Please turn on zoom to speak' }}
        </p>
        <button
          @click="showModal = false"
          style="
            padding: 0.6rem 1.4rem;
            background-color: rgba(21, 195, 154, 1);
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 1rem;
            transition: background-color 0.2s;
          ">
          Got it
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import axios from 'axios'
import {mapGetters} from 'vuex' // Still needed for translation

export default {
  computed: {
    ...mapGetters('student', ['speakChecked', 'studentSpokenText']),
  },

  data() {
    return {
      isListening: false,
      selectedLang: 'en-US',
      fullText: '',
      translatedText: '',
      recognition: null,
      pauseTimer: null,
      restartTimer: null,
      synth: window.speechSynthesis,
      showModal: false,
      languageOptions: [
        {code: 'en-US', name: 'English (US)'},
        {code: 'de-DE', name: 'German (Germany)'},
        {code: 'it-IT', name: 'Italian (Italy)'},
        {code: 'ru-RU', name: 'Russian (Russia)'},
        {code: 'ja-JP', name: 'Japanese (Japan)'},
        {code: 'ko-KR', name: 'Korean (South Korea)'},
        {code: 'hi-IN', name: 'Hindi (India)'},
        {code: 'zh-CN', name: 'Chinese (Simplified, China)'},
      ],
    }
  },

  watch: {
    speakChecked(newVal) {
      console.log('watcher triggered:', newVal)
      this.isListening = newVal
    },

    async isListening(newVal) {
      this.showModal = true
      try {
        const response = await App.service('auth').patch('syncTeacher', {
          commit: 'teacher/setSpeakChecked',
          data: newVal,
        })
        store.dispatch('teacher/setSpeakChecked', response.data)

        if (newVal) {
          this.showModal = true
          this.startRecognition()
        } else {
          this.stopRecognition()
        }
      } catch (error) {
        console.error('Error in patch:', error)
      }
    },

    selectedLang() {
      this.isListening = false
    },

    studentSpokenText: {
      handler(val) {
        if (val?.spokenText) {
          this.handleTranslation(val.spokenText)
        }
      },
      immediate: true,
      deep: true,
    },

    translatedText(val) {
      if (val) {
        this.textToSpeech(val)
      }
    },
  },

  mounted() {
    const browserLang = navigator.language || navigator.userLanguage
    let matchedLang = this.languageOptions.find((opt) => opt.code === browserLang)

    if (!matchedLang) {
      const baseLang = browserLang.split('-')[0]
      matchedLang = this.languageOptions.find((opt) => opt.code.startsWith(baseLang))
    }

    if (matchedLang) {
      this.selectedLang = matchedLang.code
    }

    this.initRecognition()

    // preload voices
    const voices = this.synth.getVoices()
    if (voices.length === 0) {
      this.synth.onvoiceschanged = () => {
        this.synth.getVoices()
      }
      setTimeout(() => {
        this.synth.getVoices()
      }, 100)
    }

    // Patch speakChecked to false
    App.service('auth')
      .patch('syncTeacher', {
        commit: 'teacher/setSpeakChecked',
        data: false,
      })
      .then((response) => {
        store.dispatch('teacher/setSpeakChecked', response.data)
      })
      .catch((error) => {
        console.error('Error patching speakChecked:', error)
      })

    // Patch spoken text to ''
    App.service('auth')
      .patch('syncTeacher', {
        commit: 'teacher/setTeacherSpokenText',
        data: {spokenText: '', selectedLang: this.selectedLang},
      })
      .then((response) => {
        store.dispatch('teacher/setTeacherSpokenText', response.data)
      })
      .catch((error) => {
        console.error('Error patching teacher spoken text:', error)
      })
  },

  methods: {
    initRecognition() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      if (!SpeechRecognition) {
        alert('Speech Recognition not supported.')
        return
      }

      this.recognition = new SpeechRecognition()
      this.recognition.continuous = true
      this.recognition.interimResults = true

      this.recognition.onresult = (event) => {
        const result = event.results[event.results.length - 1]
        const transcript = result?.[0]?.transcript?.trim()
        if (!transcript) return

        this.fullText = transcript

        if (this.pauseTimer) clearTimeout(this.pauseTimer)

        this.pauseTimer = setTimeout(() => {
          if (this.fullText && this.isListening) {
            this.sendSpokenText(this.fullText)
            this.fullText = ''
          }
        }, 1000)
      }

      this.recognition.onend = () => {
        if (this.isListening) {
          this.startRecognition()
        }
      }
      this.recognition.onaudioend = () => console.warn('Audio input ended')
      this.recognition.onspeechend = () => console.warn('Speech ended')
      this.recognition.onsoundend = () => console.warn('Sound ended')
    },

    startRecognition() {
      if (!this.recognition) {
        this.initRecognition()
      }

      this.recognition.lang = this.selectedLang
      this.recognition.start()

      this.restartTimer = setTimeout(() => {
        if (this.isListening) {
          this.recognition.stop()
        }
      }, 50000) // restart every 50s to prevent Chrome freeze
    },

    stopRecognition() {
      this.isListening = false

      if (this.recognition) {
        // Temporarily disable auto-restart to allow real stop
        const originalOnEnd = this.recognition.onend
        this.recognition.onend = null

        try {
          this.recognition.stop()
        } catch (e) {
          console.warn('Error stopping recognition:', e)
        }

        // Restore onend after a short delay
        setTimeout(() => {
          this.recognition.onend = originalOnEnd
        }, 1000)
      }

      if (this.pauseTimer) {
        clearTimeout(this.pauseTimer)
        this.pauseTimer = null
      }

      if (this.restartTimer) {
        clearTimeout(this.restartTimer)
        this.restartTimer = null
      }
    },
    async handleTranslation(text) {
      await this.translateText(text)
    },
    async sendSpokenText(spokenText) {
      try {
        const response = await App.service('auth').patch('syncTeacher', {
          commit: 'teacher/setTeacherSpokenText',
          data: {spokenText, selectedLang: this.selectedLang},
        })
        store.dispatch('teacher/setTeacherSpokenText', response.data)
      } catch (error) {
        console.error('Error in patch:', error)
      }
    },
    async translateText(text) {
      try {
        const response = await this.$app.service('translate').create({
          text: text,
          target: this.selectedLang,
        })
        this.translatedText = response?.translatedText
        console.log('Translated:', this.translatedText)
      } catch (error) {
        console.error('Translation Error:', error)
      }
    },
    textToSpeech(text) {
      if (!text) return
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = this.selectedLang
      utterance.pitch = 1
      utterance.rate = 1
      window.speechSynthesis.speak(utterance)
    },
  },
}
</script>

<style scoped>
@keyframes fadeInScale {
  0% {
    opacity: 0;
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
.custom-checkbox {
  width: 18px;
  height: 18px;
  appearance: none;
  border: 2px solid rgba(21, 195, 154, 1);
  border-radius: 4px;
  cursor: pointer;
  position: relative;
}

.custom-checkbox:checked {
  background-color: rgba(21, 195, 154, 1);
}

.custom-checkbox:checked::after {
  content: '';
  position: absolute;
  top: 1px;
  left: 5px;
  width: 3px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
</style>
